import { useState } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { SearchBar } from "./documentHelper/SearchBar";
import { DocumentList } from "./documentHelper/DocumentList";
import { SaveDocumentModal } from "./documentCRUD/SaveDocumentModal";
import { UpdateDocumentModal } from "./documentCRUD/UpdateDocumentModal";
import { DesactiveDocumentModal } from "./documentCRUD/DesactiveDocumentModal";
import { useFetchDocument } from "../../../hooks/useGetDocument";
import { useFetchDocumentByStatus } from "../../../hooks/useFetchDocumentByStatus";
import { useTheme } from "@mui/material/styles";
import { toastAlert } from "../../../utils/AlertContext";

export const PanelDocument = ({ token, onCreate, onUpdate, onDelete }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [docToUpdate, setDocToUpdate] = useState(null);
  const [docToDesactive, setDocToDesactive] = useState(null);
  const { document, loading, refetch } = useFetchDocument(token);

  const [filteredDocs, setFilteredDocs] = useState(null);
  const [filterLoading, setFilterLoading] = useState(false);
  const [currentFilterView, setCurrentFilterView] = useState('activos');
  
  const { fetchByStatus } = useFetchDocumentByStatus(token);

  const handleRefetch = async () => {
    refetch();
    
    if (filteredDocs !== null) {
      setFilterLoading(true);
      let status;
      if (currentFilterView === 'activos') status = 1;
      else if (currentFilterView === 'inactivos') status = 0;
      else status = undefined;
      
      const data = await fetchByStatus(status);
      if (data && data.data) {
        setFilteredDocs(data.data);
      }
      setFilterLoading(false);
    }
  };

  const displayDocs = filteredDocs !== null ? filteredDocs : document;
  const displayLoading = loading || filterLoading;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            mb: { xs: 2, sm: 3 },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                letterSpacing: "-0.5px",
                mb: 0.5,
              }}
            >
              Gestión de Documentos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Explora, crea y administra los documentos del sistema
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsSaveModalOpen(true)}
            disableElevation
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              boxShadow: theme.shadows[2],
              "&:hover": {
                boxShadow: theme.shadows[4],
              },
            }}
          >
            Crear Documento
          </Button>
        </Box>

        <Box sx={{ width: "100%" }}>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Buscar por nombre del documento"
            token={token}
            onFilterResult={(docs, filterName) => {
              setFilteredDocs(docs);
              if (filterName) setCurrentFilterView(filterName);
            }}
            onFilterLoading={setFilterLoading}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: 0.5,
            pb: 2,
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: 4,
            },
          }}
        >
          <DocumentList
            token={token}
            documents={displayDocs}
            loading={displayLoading}
            searchQuery={searchQuery}
            onOpenUpdateModal={setDocToUpdate}
            onOpenDesactiveModal={setDocToDesactive}
            isInactiveView={currentFilterView === 'inactivos'}
          />

        </Box>
      </Paper>
      
      <SaveDocumentModal 
        isOpen={isSaveModalOpen} 
        onClose={() => setIsSaveModalOpen(false)} 
        token={token} 
        onSuccess={(data) => {
          toastAlert("Documento creado exitosamente", "success");
          handleRefetch();
          if (onCreate) onCreate(data);
        }}
      />
      <UpdateDocumentModal
        isOpen={!!docToUpdate}
        onClose={() => setDocToUpdate(null)}
        token={token}
        documentData={docToUpdate}
        onSuccess={(data) => {
          toastAlert("Documento actualizado exitosamente", "success");
          handleRefetch();
          if (onUpdate) onUpdate(data);
          setDocToUpdate(null);
        }}
      />
      <DesactiveDocumentModal
        isOpen={!!docToDesactive}
        onClose={() => setDocToDesactive(null)}
        token={token}
        documentData={docToDesactive}
        onSuccess={(data) => {
          const isRestore = docToDesactive?.FLG_ACTIVO === 0 || docToDesactive?.FLG_ACTIVO === "0";
          toastAlert(
            isRestore ? "Documento restaurado exitosamente" : "Documento eliminado exitosamente", 
            "success"
          );
          handleRefetch();
          if (!isRestore && onDelete) onDelete(docToDesactive.ID_DOCUMENTO);
          setDocToDesactive(null);
        }}
      />
    </Box>
  );
};
